import { useEffect, useMemo, useRef, useState } from 'react';
import type { Map } from 'google-maps-react-markers';
import { useRouter } from 'next/router';
import { CLINIC_MARKETPLACE_PAGE, getEventUrl } from 'constants/pages';
import {
  EventAgeGroupsEnum,
  GetClinicsByGeoQuery,
  SkillLevelValuesEnum,
  SportsEnum,
  useGetClinicsByGeoLazyQuery,
  useGetFeaturedClinicBoostsLazyQuery,
  useGetRfxClassesByGeoLazyQuery,
} from 'types/generated/client';
import { GetCityClinicsQuery } from 'types/generated/server';
import { trackEvent } from 'services/client/analytics';
import { CLINIC_DETAIL_MODAL_OPENED } from 'services/client/analytics/events';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { convertUnitAmountToFormattedPrice } from 'utils/shared/money/convertFormattedPriceToUnitAmount';
import { isWithinPriceRange } from 'utils/shared/money/isWithinPriceRange';
import {
  getVenueDayWindow,
  isInTimeOfDay,
  isOnOrAfterVenueDay,
  isOnVenueDay,
} from 'utils/shared/time/venueDayFilters';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useInView } from 'hooks/useInView';
import { useModal } from 'hooks/useModal';
import { useQueryParam } from 'hooks/useQueryParam';
import { useVairProBadges } from 'hooks/useVairProBadges';
import { VENUE_DISTANCE_IMPERIAL_OPTIONS } from 'screens/CourtFinder/types';
import { MAX_PRICE, MIN_PRICE } from 'components/Explore/Lessons/constants';
import { CertificationEnum } from 'components/Explore/Lessons/props';
import { GoogleMapProps } from 'components/GoogleMap/props';
import { ClinicDetailModal, ClinicFilters, ClinicsList } from './components';
import VenueClinicMapMarker from './components/VenueClinicMapMarker';
import { CLINIC_TYPE_MATCH, DATE_RANGE_BUFFER, MAX_FEATURED_CLINICS } from './constants';
import { ClinicTypeFilter, CourtTypeFilter, SortByEnum, TimeEnum } from './enums';
import { getClinicLinkType } from './getClinicLinkType';
import { ClinicType, FeaturedClinic } from './props';
import { toClinicRow } from './toClinicRow';
import { groupClinicsByDate } from './utils';

export interface UseClinicsProps {
  initialSport?: SportsEnum;
  initialCityCoordinates?: {
    latitude: number;
    longitude: number;
  };
  serverClinicData?: {
    clinics: GetCityClinicsQuery['lessons'];
  };
}

const useClinics = ({
  initialSport,
  initialCityCoordinates,
  serverClinicData,
}: UseClinicsProps = {}) => {
  const { position, centerLatitude, centerLongitude, isExactCenter } = useGeoLocation();
  const router = useRouter();
  const hasDataFromServer = !!serverClinicData?.clinics.length;

  const [selectedClinicForDetail, setSelectedClinicForDetail] = useState<ClinicType | null>(null);

  const [selectedTime, setSelectedTime] = useState<TimeEnum>(TimeEnum.All);
  const [distance, setDistance] = useState(VENUE_DISTANCE_IMPERIAL_OPTIONS[2]);
  const [sortBy, setSortBy] = useState<SortByEnum>(SortByEnum.NearestTime);
  const [isOpenMap, setIsOpenMap] = useState<boolean>(false);
  const [minPrice, setMinPrice] = useState<number>(MIN_PRICE);
  const [maxPrice, setMaxPrice] = useState<number>(MAX_PRICE);
  const [selectedCertification, setSelectedCertification] = useState<CertificationEnum | null>(
    null,
  );
  const [isYouthProgramming, setIsYouthProgramming] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<ClinicTypeFilter[]>([]);
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<SkillLevelValuesEnum | null>(null);
  const [selectedCourtTypes, setSelectedCourtTypes] = useState<CourtTypeFilter[]>([]);
  const discoverContainerRef = useRef<HTMLDivElement | null>(null);
  const [listHoveredClinicId, setListHoveredClinicId] = useState<string | null>(null);
  const [listHoveredVenueId, setListHoveredVenueId] = useState<string | null>(null);
  const [activeVenueId, setActiveVenueId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const clinicDetailModal = useModal();
  const mapRef = useRef<Map>();
  const isDiscoverSectionInView = useInView(discoverContainerRef);
  const [getClinicsByGeo, { loading, data, called }] = useGetClinicsByGeoLazyQuery();
  const [getFeaturedClinicBoosts, { data: featuredData }] = useGetFeaturedClinicBoostsLazyQuery();
  const [getRfxClasses, { data: rfxData }] = useGetRfxClassesByGeoLazyQuery();

  const [selectedSport, setSelectedSport, isSportReady] = useQueryParam('sport', {
    defaultValue: initialSport || SportsEnum.Pickleball,
    parser: (value: string) => {
      const sport = Object.values(SportsEnum).find(
        (val) => val.toLowerCase() === value.toLowerCase(),
      );
      return sport || SportsEnum.Pickleball;
    },
    serializer: (value: SportsEnum) => value.toLowerCase(),
  });

  const handleToggleMap = () => {
    setIsOpenMap((curr) => !curr);
  };

  const decoratedClinics = useMemo(() => {
    // `serverClinicData.clinics` is typed from the server-side `getCityClinics` query, a
    // separate (nominal) copy of the schema — the same clinic shape at runtime but not
    // assignable to the client `ClinicType`. Cast at this boundary so the rest of the hook
    // works against a single client-typed clinic list.
    const clinicsData =
      (serverClinicData?.clinics as unknown as GetClinicsByGeoQuery['lessons']) || data?.lessons;
    const distanceTo = (venue?: { geometry?: any } | null) =>
      calculateHaversineDistance({
        coord1: {
          latitude: initialCityCoordinates?.latitude || centerLatitude || 0,
          longitude: initialCityCoordinates?.longitude || centerLongitude || 0,
        },
        coord2: {
          latitude: venue?.geometry.coordinates[1],
          longitude: venue?.geometry.coordinates[0],
        },
        unit: 'miles',
      });
    const lessons: ClinicType[] = (clinicsData ?? []).map((lesson) => ({
      ...lesson,
      distance: distanceTo(lesson.venue),
    }));
    // RFX classes are fetched on the client even on server-rendered city pages.
    const rfxClasses = (rfxData?.events ?? []).map((event) =>
      toClinicRow(event, distanceTo(event.venue)),
    );
    return [...lessons, ...rfxClasses];
  }, [
    serverClinicData?.clinics,
    data?.lessons,
    rfxData?.events,
    initialCityCoordinates,
    centerLatitude,
    centerLongitude,
  ]);

  /**
   * VAIR PRO status for the coaches who own these clinics. It is a separate programme
   * from the racquet registry and lives on the VAIR API rather than on `UserProfiles`,
   * so the VAIR PRO certification tile cannot be answered by
   * `isProfessionalRacquetRegistryActive` — see the same lookup in
   * `components/Explore/Lessons/useLesson`.
   *
   * Sourced from the unfiltered clinic list so narrowing by the tile never shrinks the
   * id set the map is built from.
   */
  const ownerUserIds = useMemo(() => {
    const ids = decoratedClinics
      .map((clinic) => clinic.ownerProfile?.id)
      .filter((id): id is string => !!id);
    return Array.from(new Set(ids));
  }, [decoratedClinics]);

  const ownerVairProMap = useVairProBadges(ownerUserIds);

  const featuredClinics = useMemo<FeaturedClinic[]>(() => {
    const boosts = featuredData?.boosts ?? [];
    const centerLat = initialCityCoordinates?.latitude || centerLatitude || 0;
    const centerLng = initialCityCoordinates?.longitude || centerLongitude || 0;

    const relevant = boosts.filter(
      (boost) => boost.lesson && (!selectedSport || boost.lesson.sport === selectedSport),
    );
    // Boosts on the selected day are the most relevant — show ALL of them,
    // uncapped. Only when there are none do we fall back to a small set of other
    // upcoming boosts (query order = startsAt DESC).
    const sameDay = relevant.filter((boost) => isOnVenueDay(boost.lesson!, selectedDate));
    const selected = sameDay.length > 0 ? sameDay : relevant.slice(0, MAX_FEATURED_CLINICS);

    return selected.map((boost) => {
      const lesson = boost.lesson!;
      return {
        placementId: boost.id,
        clinic: {
          ...lesson,
          distance: calculateHaversineDistance({
            coord1: { latitude: centerLat, longitude: centerLng },
            coord2: {
              latitude: lesson.venue?.geometry.coordinates[1],
              longitude: lesson.venue?.geometry.coordinates[0],
            },
            unit: 'miles',
          }),
        },
      };
    });
  }, [
    featuredData,
    selectedSport,
    selectedDate,
    initialCityCoordinates,
    centerLatitude,
    centerLongitude,
  ]);

  // Boosted clinics surface only in the featured frame — drop them from the
  // regular list so the same card doesn't appear twice.
  const featuredIds = useMemo(
    () => new Set(featuredClinics.map((f) => f.clinic.id)),
    [featuredClinics],
  );

  // Featured clinics come from a separate (date-unbounded) query, so merge them
  // back in for the map — otherwise a boosted card has no marker. Dedup by id.
  const mappableClinics = useMemo(() => {
    const merged = featuredClinics.map((f) => f.clinic);
    decoratedClinics.forEach((clinic) => {
      if (!featuredIds.has(clinic.id)) merged.push(clinic);
    });
    return merged;
  }, [featuredClinics, featuredIds, decoratedClinics]);

  // Group clinics by venue
  const clinicsByVenue = useMemo(() => {
    const venueMap = new Map<
      string,
      {
        venueId: string;
        venueName: string;
        lat: number;
        lng: number;
        clinics: ClinicType[];
      }
    >();

    mappableClinics.forEach((clinic) => {
      const venue = clinic.venue;
      if (!venue?.geometry?.coordinates || !venue.id) return;

      const lat = venue.geometry.coordinates[1];
      const lng = venue.geometry.coordinates[0];
      // Create a unique key based on venue ID
      const venueKey = venue.id;
      const venueName = venue.title || venue.addressString || 'Venue';

      if (!venueMap.has(venueKey)) {
        venueMap.set(venueKey, {
          venueId: venueKey,
          venueName,
          lat,
          lng,
          clinics: [],
        });
      }

      venueMap.get(venueKey)!.clinics.push(clinic);
    });

    return Array.from(venueMap.values());
  }, [mappableClinics]);

  const clinicsMapProps: GoogleMapProps = {
    syncDragToURL: true,
    center: {
      lat: initialCityCoordinates?.latitude || centerLatitude,
      lng: initialCityCoordinates?.longitude || centerLongitude,
    },
    options: {
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      disableDefaultUI: true,
      clickableIcons: false,
    },

    centerIcon: (
      <div className="h-8 w-8 rounded-full border-[12px] border-color-border-brand bg-white" />
    ),
    zoom: 12,
    markers: clinicsByVenue.map((venueData) => ({
      lat: venueData.lat,
      lng: venueData.lng,
      markerId: venueData.venueId,
      zIndex:
        activeVenueId === venueData.venueId ? 3 : listHoveredVenueId === venueData.venueId ? 2 : 1,
      children: (
        <VenueClinicMapMarker
          venueId={venueData.venueId}
          venueName={venueData.venueName}
          clinics={venueData.clinics}
          lat={venueData.lat}
          lng={venueData.lng}
          listHoveredVenueId={listHoveredVenueId}
          setListHoveredVenueId={setListHoveredVenueId}
          setActiveVenueId={setActiveVenueId}
          activeVenueId={activeVenueId}
          onClinicClick={(clinic) => {
            setActiveVenueId(null);
            handleClinicDetail(clinic);
          }}
        />
      ),
    })),
    isExactCenter,
    onCenterChange: ({ lat, lng }) => {
      navigateToClinicMarketplace({ latitude: lat, longitude: lng });
    },
  };

  const handleGoogleApiLoaded = (map: Map) => {
    mapRef.current = map;
    map.setOptions({
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      },
    });
  };

  const handleClinicDetail = (clinic: ClinicType) => {
    // An RFX class has its own registration flow on the event page, so no lesson modal.
    if (clinic.rfxEvent) {
      router.push(getEventUrl(clinic.rfxEvent.slug));
      return;
    }

    // Track clinic detail modal opened event
    const linkType = getClinicLinkType(clinic);
    trackEvent(CLINIC_DETAIL_MODAL_OPENED, {
      clinic_id: clinic.id,
      clinic_title: clinic.title,
      link_type: linkType,
      sport: clinic.sport,
      external_source: clinic.externalSource || null,
      has_external_booking_url: !!clinic.externalBookingUrl?.trim(),
      price_unit_amount: clinic.priceUnitAmount,
      participant_count: clinic.participants?.length || 0,
      participant_limit: clinic.participantLimit || 0,
      venue_name: clinic.venue?.title || clinic.userCustomCourt?.fullAddress || null,
      coach_name: clinic.ownerProfile?.fullName || null,
      start_date_time: clinic.startDateTime,
    });

    setSelectedClinicForDetail(clinic);
    clinicDetailModal.openModal();
  };

  const handleClearAllFilters = () => {
    setSelectedSport(SportsEnum.Pickleball);
    setSelectedTime(TimeEnum.All);
    setDistance(VENUE_DISTANCE_IMPERIAL_OPTIONS[0]);
    setSortBy(SortByEnum.PlayersGoing);
    setMinPrice(MIN_PRICE);
    setMaxPrice(MAX_PRICE);
    setSelectedCertification(null);
    setSelectedDate(new Date());
    setIsYouthProgramming(false);
    setSelectedTypes([]);
    setSelectedSkillLevel(null);
    setSelectedCourtTypes([]);
  };

  const navigateToClinicMarketplace = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    if (serverClinicData)
      router.push(
        `${CLINIC_MARKETPLACE_PAGE}?sport=${selectedSport.toLocaleLowerCase()}&lat=${latitude}&lng=${longitude}`,
      );
  };

  const filterClinics = (clinics: ClinicType[]): ClinicType[] => {
    return clinics.filter((clinic) => {
      // Sport filter
      if (selectedSport && clinic.sport !== selectedSport) {
        return false;
      }

      // Filter by price range — use the lesson's actual price, not the coach's default,
      // so external listings (which may have no coach default) are filtered correctly.
      const price = clinic.priceUnitAmount;
      const currency =
        clinic.currency ||
        clinic.ownerProfile?.stripeMerchantCurrency ||
        clinic.ownerProfile?.currencyCoachDefault;
      if (price != null && currency) {
        const normalisedPrice = convertUnitAmountToFormattedPrice(price, currency);
        if (
          !isWithinPriceRange(normalisedPrice, {
            minimum: minPrice,
            maximum: maxPrice,
            sliderMaximum: MAX_PRICE,
          })
        ) {
          return false;
        }
      }

      // Certification filter. VAIR PRO is not a racquet-registry certification — it is
      // answered by the VAIR API's `isVairPro` instead of the registry flag.
      if (selectedCertification === CertificationEnum.VAIR_PRO) {
        const ownerId = clinic.ownerProfile?.id;
        if (!ownerId || !ownerVairProMap[ownerId]) {
          return false;
        }
      } else if (
        selectedCertification &&
        !clinic.ownerProfile?.isProfessionalRacquetRegistryActive
      ) {
        return false;
      }

      // Youth programming — only clinics explicitly intended for youth.
      if (isYouthProgramming && clinic.intendedAgeGroup !== EventAgeGroupsEnum.Youth) {
        return false;
      }

      // Type filter — tabs are OR'd (see CLINIC_TYPE_MATCH).
      if (
        selectedTypes.length > 0 &&
        !selectedTypes.some((type) => CLINIC_TYPE_MATCH[type](clinic))
      ) {
        return false;
      }

      // Skill level — a clinic with no declared levels is open to everyone, so it
      // stays visible rather than being filtered out.
      if (selectedSkillLevel) {
        const levels = clinic.skillLevels ?? [];
        if (levels.length > 0 && !levels.some((level) => level.skillLevel === selectedSkillLevel)) {
          return false;
        }
      }

      // Court type — matched on the venue's court counts, same as ExploreOpenPlay.
      if (selectedCourtTypes.length > 0) {
        const indoor = clinic.venue?.indoorCourtCount ?? 0;
        const outdoor = clinic.venue?.outdoorCourtCount ?? 0;
        const matchesCourtType = selectedCourtTypes.some((type) =>
          type === CourtTypeFilter.Indoor ? indoor > 0 : outdoor > 0,
        );
        if (!matchesCourtType) {
          return false;
        }
      }

      if (!isOnOrAfterVenueDay(clinic, selectedDate)) {
        return false;
      }

      // Time of day filter
      if (!isInTimeOfDay(clinic, selectedTime)) {
        return false;
      }

      return true;
    });
  };

  const sortClinics = (clinics: ClinicType[]): ClinicType[] => {
    const sortedClinics = [...clinics];

    switch (sortBy) {
      case SortByEnum.CoachRating:
        return sortedClinics.sort((a, b) => {
          const ratingA = a.ownerProfile?.coachReviewsAggregate?.aggregate?.avg?.rating ?? 0;
          const ratingB = b.ownerProfile?.coachReviewsAggregate?.aggregate?.avg?.rating ?? 0;
          return ratingB - ratingA; // Higher rating first
        });

      case SortByEnum.PriceLowHigh:
        return sortedClinics.sort((a, b) => a.priceUnitAmount - b.priceUnitAmount);

      case SortByEnum.PriceHighLow:
        return sortedClinics.sort((a, b) => b.priceUnitAmount - a.priceUnitAmount);

      case SortByEnum.NearestTime:
        return sortedClinics.sort((a, b) => {
          const timeA = new Date(a.startDateTime).getTime();
          const timeB = new Date(b.startDateTime).getTime();
          return timeA - timeB; // Earlier time first
        });

      case SortByEnum.PlayersGoing:
        return sortedClinics.sort((a, b) => {
          return (b.participants?.length || 0) - (a.participants?.length || 0); // More participants first
        });

      case SortByEnum.Distance:
        return sortedClinics.sort((a, b) => a.distance - b.distance);

      default:
        return sortedClinics;
    }
  };

  // First filter, then sort the clinics
  const filteredAndSortedClinics = useMemo(() => {
    const withoutFeatured = decoratedClinics.filter((clinic) => !featuredIds.has(clinic.id));
    const filtered = filterClinics(withoutFeatured);
    return sortClinics(filtered);
  }, [
    decoratedClinics,
    featuredIds,
    selectedSport,
    minPrice,
    maxPrice,
    selectedCertification,
    ownerVairProMap,
    selectedDate,
    selectedTime,
    distance,
    sortBy,
    isYouthProgramming,
    selectedTypes,
    selectedSkillLevel,
    selectedCourtTypes,
  ]);

  const filteredSortedAndGroupedByDateClinics = useMemo(
    () => groupClinicsByDate(filteredAndSortedClinics),
    [filteredAndSortedClinics],
  );

  useEffect(() => {
    if (discoverContainerRef.current && isOpenMap)
      discoverContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
  }, [isOpenMap]);

  useEffect(() => {
    // Only fetch clinics if server data is not available
    if (!serverClinicData && position) {
      const init = async () => {
        const { startDate, endDate } = getVenueDayWindow({
          selectedDate,
          forwardDays: DATE_RANGE_BUFFER,
        });

        await getClinicsByGeo({
          fetchPolicy: 'network-only',
          variables: {
            distance: milesToMeters(distance.id),
            from: {
              type: 'Point',
              coordinates: [position.longitude, position.latitude],
            },
            startDate,
            endDate,
          },
        });
      };

      init();
    }
  }, [getClinicsByGeo, position, distance, selectedDate, serverClinicData]);

  // RFX classes come from `events`, so they are fetched beside the lessons for the same
  // area and window — from the viewer's position, or the city on server-rendered pages.
  useEffect(() => {
    const from = position
      ? { latitude: position.latitude, longitude: position.longitude }
      : initialCityCoordinates;
    if (!from) return;

    const { startDate, endDate } = getVenueDayWindow({
      selectedDate,
      forwardDays: DATE_RANGE_BUFFER,
    });
    getRfxClasses({
      fetchPolicy: 'network-only',
      variables: {
        distance: milesToMeters(distance.id),
        from: { type: 'Point', coordinates: [from.longitude, from.latitude] },
        startDate,
        endDate,
      },
    });
  }, [getRfxClasses, position, initialCityCoordinates, distance, selectedDate]);

  // Boosted clinics for the area — intentionally NOT bounded by the selected date.
  // A live boost is always worth an impression (dates are fluid; supply is sparse
  // at launch), so it surfaces regardless of the user's date filter. Uses the live
  // viewer position when browsing, or the city coordinates on server-rendered city
  // pages, so the featured frame shows in both contexts.
  useEffect(() => {
    const from = position
      ? { latitude: position.latitude, longitude: position.longitude }
      : initialCityCoordinates;
    if (!from) return;

    getFeaturedClinicBoosts({
      fetchPolicy: 'network-only',
      variables: {
        distance: milesToMeters(distance.id),
        from: {
          type: 'Point',
          coordinates: [from.longitude, from.latitude],
        },
        now: new Date().toISOString(),
      },
    });
  }, [getFeaturedClinicBoosts, position, initialCityCoordinates, distance]);

  return {
    handleToggleMap,
    selectedSport,
    distance,
    setSelectedSport,
    sortBy,
    setSortBy,
    setDistance,
    clinicsMapProps,
    handleGoogleApiLoaded,
    isOpenMap,
    discoverContainerRef,
    isDiscoverSectionInView,
    setSelectedDate,
    selectedDate,
    handleClearAllFilters,
    selectedTime,
    setSelectedTime,
    clinicsLength: filteredAndSortedClinics.length,
    isSportReady,

    ClinicDetailModal: (
      <ClinicDetailModal
        open={clinicDetailModal.isOpen}
        handleClose={clinicDetailModal.closeModal}
        clinic={selectedClinicForDetail as ClinicType}
      />
    ),

    ClinicsList: (
      <ClinicsList
        listHoveredClinicId={listHoveredClinicId}
        setListHoveredClinicId={setListHoveredClinicId}
        setListHoveredVenueId={setListHoveredVenueId}
        loading={hasDataFromServer ? false : loading}
        called={hasDataFromServer ? true : called}
        list={filteredSortedAndGroupedByDateClinics.groupedClinics}
        featuredClinics={featuredClinics}
        onClinicDetail={handleClinicDetail}
        dates={filteredSortedAndGroupedByDateClinics.dates}
        selectedDate={selectedDate}
        handleClearAllFilters={handleClearAllFilters}
      />
    ),
    ClinicFilters: (
      <ClinicFilters
        setPriceMinimum={setMinPrice}
        priceMinimum={minPrice}
        setPriceMaximum={setMaxPrice}
        priceMaximum={maxPrice}
        setSelectedCertification={setSelectedCertification}
        selectedCertification={selectedCertification}
        setDistance={setDistance}
        distance={distance}
        selectedSport={selectedSport}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        setSelectedTime={setSelectedTime}
        selectedTime={selectedTime}
        sortBy={sortBy}
        setSortBy={setSortBy}
        isYouthProgramming={isYouthProgramming}
        setIsYouthProgramming={setIsYouthProgramming}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
        selectedSkillLevel={selectedSkillLevel}
        setSelectedSkillLevel={setSelectedSkillLevel}
        selectedCourtTypes={selectedCourtTypes}
        setSelectedCourtTypes={setSelectedCourtTypes}
      />
    ),
    onAddressChange: ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      navigateToClinicMarketplace({ latitude, longitude });
    },
  };
};

export default useClinics;
